﻿import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { starterPackageApi, inventoryApi, customerApi, orderApi } from '../../services/api'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'

/**
 * Starter Packages — build reusable GHASC "starter kits" and issue them to
 * new (or existing) groups. Issuing creates a GHASC-covered order: inventory
 * is deducted, shortfalls backorder like any order, and a receipt is emailed
 * to the literature chair.
 *
 * One flow: a list of packages, each with Edit and Issue. Creating/editing and
 * issuing both happen inline under the relevant card — no separate pages.
 */
export default function StarterPackages() {
    const [packages, setPackages] = useState([])
    const [loading, setLoading] = useState(true)

    // Which inline panel is open. { type: 'new' | 'edit' | 'issue', id }
    const [panel, setPanel] = useState(null)

    useEffect(() => { load() }, [])

    async function load() {
        setLoading(true)
        try {
            const response = await starterPackageApi.list()
            if (response.success) setPackages(response.packages || [])
            else toast.error(response.message || 'Could not load packages')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    function closePanel() { setPanel(null) }

    async function afterSave() {
        closePanel()
        await load()
    }

    async function handleToggleActive(pkg) {
        try {
            const response = await starterPackageApi.setActive(pkg.id, !pkg.isActive)
            if (response.success) {
                toast.success(pkg.isActive ? `"${pkg.name}" deactivated` : `"${pkg.name}" activated`)
                load()
            } else {
                toast.error(response.message || 'Could not update')
            }
        } catch {
            toast.error('Network error')
        }
    }

    async function handleDelete(pkg) {
        if (!confirm(`Delete "${pkg.name}"? This removes the template. Any packages already issued keep their history.`)) return
        try {
            const response = await starterPackageApi.delete(pkg.id)
            if (response.success) {
                toast.success('Package deleted')
                if (panel?.id === pkg.id) closePanel()
                load()
            } else {
                toast.error(response.message || 'Could not delete')
            }
        } catch {
            toast.error('Network error')
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Starter Packages</h2>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Build reusable kits for new groups, then issue one to a group. Issuing
                        deducts inventory and emails a GHASC-covered receipt to the literature chair.
                        Anything short of stock is backordered automatically.
                    </p>
                </div>
                <Button
                    onClick={() => setPanel(panel?.type === 'new' ? null : { type: 'new' })}
                    disabled={panel?.type === 'edit' || panel?.type === 'issue'}
                >
                    New Package
                </Button>
            </div>

            {/* New-package editor lives at the top */}
            {panel?.type === 'new' && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-ghasc-navy mb-4">New Package</h3>
                    <PackageEditor packageId={null} onSaved={afterSave} onCancel={closePanel} />
                </div>
            )}

            {loading ? (
                <div className="text-center text-gray-500 py-8">Loading packages...</div>
            ) : packages.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-600">
                    No starter packages yet. Click <span className="font-medium">New Package</span> to build one.
                </div>
            ) : (
                <div className="space-y-3">
                    {packages.map(pkg => (
                        <div key={pkg.id} className="bg-white rounded-xl shadow-sm">
                            {/* Card header */}
                            <div className="p-4 flex items-start justify-between gap-4 flex-wrap">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-ghasc-navy font-semibold">{pkg.name}</span>
                                        {!pkg.isActive && (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                                                Inactive
                                            </span>
                                        )}
                                    </div>
                                    {pkg.description && (
                                        <div className="text-sm text-gray-500 mt-0.5">{pkg.description}</div>
                                    )}
                                    <div className="text-xs text-gray-500 mt-1">
                                        {pkg.lineCount} item{pkg.lineCount === 1 ? '' : 's'} · {pkg.totalUnits} unit{pkg.totalUnits === 1 ? '' : 's'}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 flex-wrap">
                                    <Button
                                        onClick={() => setPanel(
                                            panel?.type === 'issue' && panel.id === pkg.id
                                                ? null
                                                : { type: 'issue', id: pkg.id }
                                        )}
                                        disabled={!pkg.isActive || pkg.lineCount === 0}
                                    >
                                        Issue
                                    </Button>
                                    <button
                                        onClick={() => setPanel(
                                            panel?.type === 'edit' && panel.id === pkg.id
                                                ? null
                                                : { type: 'edit', id: pkg.id }
                                        )}
                                        className="px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                    >
                                        {panel?.type === 'edit' && panel.id === pkg.id ? 'Close' : 'Edit'}
                                    </button>
                                    <button
                                        onClick={() => handleToggleActive(pkg)}
                                        className="px-3 py-2 text-sm text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100"
                                    >
                                        {pkg.isActive ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(pkg)}
                                        className="px-3 py-2 text-sm text-ghasc-danger bg-gray-50 rounded-lg hover:bg-gray-100"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            {/* Inline edit panel */}
                            {panel?.type === 'edit' && panel.id === pkg.id && (
                                <div className="border-t border-gray-100 p-4">
                                    <PackageEditor packageId={pkg.id} onSaved={afterSave} onCancel={closePanel} />
                                </div>
                            )}

                            {/* Inline issue panel */}
                            {panel?.type === 'issue' && panel.id === pkg.id && (
                                <div className="border-t border-gray-100 p-4">
                                    <IssuePanel
                                        packageId={pkg.id}
                                        packageName={pkg.name}
                                        onDone={closePanel}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// ===========================================================================
//  PACKAGE EDITOR — name, description, and the line set
// ===========================================================================
function PackageEditor({ packageId, onSaved, onCancel }) {
    const isEdit = packageId != null

    const [loading, setLoading] = useState(isEdit)
    const [saving, setSaving] = useState(false)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')

    // Lines keyed by inventory id → { inventoryId, quantity, ...display }
    const [lines, setLines] = useState({})

    // Picker state
    const [showPicker, setShowPicker] = useState(false)
    const [pickerSearch, setPickerSearch] = useState('')
    const [allItems, setAllItems] = useState([])
    const [allItemsLoaded, setAllItemsLoaded] = useState(false)

    useEffect(() => {
        if (isEdit) loadPackage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId])

    async function loadPackage() {
        setLoading(true)
        try {
            const response = await starterPackageApi.getById(packageId)
            if (response.success) {
                const p = response.package
                setName(p.name || '')
                setDescription(p.description || '')
                const next = {}
                for (const it of p.items) {
                    next[it.inventoryId] = lineFromPackageItem(it)
                }
                setLines(next)
            } else {
                toast.error(response.message || 'Could not load package')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function loadAllItems() {
        if (allItemsLoaded) return
        try {
            const response = await inventoryApi.list({})
            if (response.success) {
                setAllItems(response.items)
                setAllItemsLoaded(true)
            }
        } catch { /* non-fatal */ }
    }

    function setQty(inventoryId, quantity) {
        setLines(prev => ({
            ...prev,
            [inventoryId]: { ...prev[inventoryId], quantity },
        }))
    }

    function removeLine(inventoryId) {
        setLines(prev => {
            const next = { ...prev }
            delete next[inventoryId]
            return next
        })
    }

    function addItem(item) {
        if (lines[item.id]) {
            toast('That item is already in the package')
            return
        }
        setLines(prev => ({
            ...prev,
            [item.id]: lineFromInventory(item),
        }))
        setShowPicker(false)
        setPickerSearch('')
    }

    async function handleSave() {
        const trimmedName = name.trim()
        if (!trimmedName) {
            toast.error('Give the package a name')
            return
        }
        const items = Object.values(lines)
            .filter(l => l.quantity > 0)
            .map(l => ({ inventoryId: l.inventoryId, quantity: l.quantity }))
        if (items.length === 0) {
            toast.error('Add at least one item with a quantity')
            return
        }

        setSaving(true)
        try {
            const body = { name: trimmedName, description: description.trim() || null, isActive: true, items }
            const response = isEdit
                ? await starterPackageApi.update(packageId, body)
                : await starterPackageApi.create(body)
            if (response.success) {
                toast.success(isEdit ? 'Package updated' : 'Package created')
                onSaved(response.package)
            } else {
                toast.error(response.message || 'Save failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setSaving(false)
        }
    }

    const totals = useMemo(() => {
        let units = 0, subtotal = 0
        for (const l of Object.values(lines)) {
            if (!l.quantity) continue
            units += Number(l.quantity)
            subtotal += Number(l.cost) * Number(l.quantity)
        }
        return { units, subtotal }
    }, [lines])

    if (loading) {
        return <div className="text-center text-gray-500 py-6">Loading package...</div>
    }

    const linesArray = Object.values(lines)

    return (
        <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
                <Input
                    id="pkg-name"
                    label="Package name"
                    placeholder="e.g. New Group Starter Kit"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <Input
                    id="pkg-desc"
                    label="Description (optional)"
                    placeholder="What this kit is for"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </div>

            {linesArray.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-600">
                    No items yet. Add the items that make up this package below.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <tr>
                                <th className="px-3 py-2">Item</th>
                                <th className="px-3 py-2 w-24 text-right">Unit</th>
                                <th className="px-3 py-2 w-28 text-right">Qty</th>
                                <th className="px-3 py-2 w-28 text-right">Line</th>
                                <th className="px-3 py-2 w-10"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {linesArray.map(l => {
                                const lineTotal = Number(l.cost) * Number(l.quantity || 0)
                                const variantParts = [l.year, l.medallionType, l.color].filter(Boolean)
                                return (
                                    <tr key={l.inventoryId}>
                                        <td className="px-3 py-2">
                                            <div className="text-sm font-medium text-gray-900">{l.description}</div>
                                            <div className="text-xs text-gray-500 font-mono">
                                                {l.itemNumber}
                                                {variantParts.length > 0 && (
                                                    <span className="ml-2 font-sans">— {variantParts.join(' / ')}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-right text-sm">${Number(l.cost).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <input
                                                type="number"
                                                min="1"
                                                value={l.quantity}
                                                onChange={(e) => {
                                                    const n = parseInt(e.target.value, 10)
                                                    setQty(l.inventoryId, isNaN(n) ? 0 : n)
                                                }}
                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-right text-sm font-medium">${lineTotal.toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                onClick={() => removeLine(l.inventoryId)}
                                                title="Remove"
                                                className="text-ghasc-danger text-sm hover:underline"
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add items */}
            <div className="border-t border-gray-100 pt-4">
                {!showPicker ? (
                    <button
                        type="button"
                        onClick={() => { setShowPicker(true); loadAllItems() }}
                        className="text-sm text-ghasc-navy hover:underline"
                    >
                        + Add an item to the package
                    </button>
                ) : (
                    <ItemPicker
                        allItems={allItems}
                        loaded={allItemsLoaded}
                        existingIds={new Set(Object.keys(lines).map(Number))}
                        search={pickerSearch}
                        onSearchChange={setPickerSearch}
                        onPick={addItem}
                        onCancel={() => { setShowPicker(false); setPickerSearch('') }}
                    />
                )}
            </div>

            {/* Totals + save */}
            <div className="border-t border-gray-100 pt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-gray-600">
                    {totals.units} unit{totals.units === 1 ? '' : 's'} —{' '}
                    <span className="font-semibold text-gray-900">${totals.subtotal.toFixed(2)}</span> per package
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
                    <Button type="button" onClick={handleSave} loading={saving}>
                        {isEdit ? 'Save Changes' : 'Create Package'}
                    </Button>
                </div>
            </div>
        </div>
    )
}

// ===========================================================================
//  ISSUE PANEL — pick/type a group, review lines, confirm
// ===========================================================================
function IssuePanel({ packageId, packageName, onDone }) {
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    // Issue lines: { inventoryId, quantity, ...display, stock }
    const [lines, setLines] = useState([])
    const [notes, setNotes] = useState('')

    // Group autocomplete
    const [customers, setCustomers] = useState([])
    const [groupQuery, setGroupQuery] = useState('')
    const [selectedCustomerId, setSelectedCustomerId] = useState(null)
    const [showSuggestions, setShowSuggestions] = useState(false)

    // Success state (the created order)
    const [issued, setIssued] = useState(null)

    useEffect(() => {
        loadEverything()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId])

    async function loadEverything() {
        setLoading(true)
        try {
            const [pkgRes, custRes] = await Promise.all([
                starterPackageApi.getById(packageId),
                customerApi.list(),
            ])
            if (pkgRes.success) {
                setLines(pkgRes.package.items.map(it => ({
                    inventoryId: it.inventoryId,
                    quantity: it.quantity,
                    description: it.description,
                    itemNumber: it.itemNumber,
                    year: it.year,
                    color: it.color,
                    medallionType: it.medallionType,
                    cost: it.cost,
                    stock: it.stockQuantity,
                })))
            } else {
                toast.error(pkgRes.message || 'Could not load package')
            }
            if (custRes.success) setCustomers(custRes.customers || [])
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    function setQty(inventoryId, quantity) {
        setLines(prev => prev.map(l =>
            l.inventoryId === inventoryId ? { ...l, quantity } : l
        ))
    }

    const suggestions = useMemo(() => {
        const q = groupQuery.trim().toLowerCase()
        if (!q) return []
        return customers
            .filter(c => (c.name || '').toLowerCase().includes(q))
            .slice(0, 8)
    }, [groupQuery, customers])

    function pickCustomer(c) {
        setSelectedCustomerId(c.id)
        setGroupQuery(c.name)
        setShowSuggestions(false)
    }

    function onGroupChange(value) {
        setGroupQuery(value)
        setSelectedCustomerId(null)   // typing invalidates any prior pick
        setShowSuggestions(true)
    }

    const totals = useMemo(() => {
        let subtotal = 0, backorderLines = 0
        for (const l of lines) {
            if (!l.quantity) continue
            subtotal += Number(l.cost) * Number(l.quantity)
            if (l.quantity > l.stock) backorderLines++
        }
        return { subtotal, backorderLines }
    }, [lines])

    async function handleConfirm() {
        const trimmed = groupQuery.trim()
        if (!trimmed) {
            toast.error('Enter or pick a group')
            return
        }
        const toIssue = lines.filter(l => l.quantity > 0)
        if (toIssue.length === 0) {
            toast.error('Every line is set to zero — nothing to issue')
            return
        }

        // Resolve existing-vs-new: a selected customer whose name still matches
        // the field is treated as existing; anything else is a new group name.
        const matched = selectedCustomerId
            && customers.find(c => c.id === selectedCustomerId && c.name === trimmed)

        const body = {
            packageId,
            notes: notes.trim() || null,
            lineOverrides: toIssue.map(l => ({ inventoryId: l.inventoryId, quantity: l.quantity })),
        }
        if (matched) body.customerId = selectedCustomerId
        else body.newGroupName = trimmed

        const groupLabel = trimmed
        const backorderNote = totals.backorderLines > 0
            ? ` ${totals.backorderLines} line${totals.backorderLines === 1 ? '' : 's'} will backorder.`
            : ''
        if (!confirm(`Issue "${packageName}" to ${groupLabel} for $${totals.subtotal.toFixed(2)} (GHASC-covered)?${backorderNote}`)) {
            return
        }

        setSubmitting(true)
        try {
            const response = await starterPackageApi.issue(body)
            if (response.success) {
                toast.success('Package issued')
                setIssued(response.order)
            } else {
                toast.error(response.message || 'Could not issue package')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setSubmitting(false)
        }
    }

    async function downloadReceipt() {
        if (!issued) return
        try {
            const { blob, fileName } = await orderApi.fetchPdf(issued.id, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Could not download receipt')
        }
    }

    if (loading) {
        return <div className="text-center text-gray-500 py-6">Loading...</div>
    }

    // ----- Success view -----
    if (issued) {
        return (
            <div className="space-y-4">
                <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                    <div className="text-ghasc-navy font-semibold">
                        Issued to {issued.customerName}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                        ${Number(issued.subtotal).toFixed(2)} — GHASC-covered.{' '}
                        {issued.backorderCount > 0
                            ? `${issued.backorderCount} line${issued.backorderCount === 1 ? '' : 's'} backordered — they'll fill from the next regional shipment.`
                            : 'Everything was in stock.'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                        A receipt was emailed to the literature chair.
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="secondary" onClick={downloadReceipt}>Download receipt</Button>
                    <Button type="button" onClick={onDone}>Done</Button>
                </div>
            </div>
        )
    }

    // ----- Issue form -----
    return (
        <div className="space-y-4">
            {/* Group field with autocomplete */}
            <div className="relative max-w-md">
                <label htmlFor="issue-group" className="block text-sm font-medium text-gray-700 mb-1">
                    Group receiving the package
                </label>
                <input
                    id="issue-group"
                    type="text"
                    value={groupQuery}
                    onChange={(e) => onGroupChange(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    placeholder="Start typing — pick an existing group or type a new name"
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy focus:border-transparent"
                />
                {selectedCustomerId ? (
                    <p className="mt-1 text-xs text-ghasc-success">Existing group selected.</p>
                ) : groupQuery.trim() ? (
                    <p className="mt-1 text-xs text-gray-500">
                        No group selected — this will be recorded as a new group named "{groupQuery.trim()}".
                    </p>
                ) : null}

                {showSuggestions && suggestions.length > 0 && (
                    <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {suggestions.map(c => (
                            <li key={c.id}>
                                <button
                                    type="button"
                                    onMouseDown={(e) => { e.preventDefault(); pickCustomer(c) }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                >
                                    {c.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Review lines */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <tr>
                            <th className="px-3 py-2">Item</th>
                            <th className="px-3 py-2 w-20 text-right">In stock</th>
                            <th className="px-3 py-2 w-28 text-right">Issue qty</th>
                            <th className="px-3 py-2 w-44">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {lines.map(l => {
                            const qty = Number(l.quantity || 0)
                            const willBackorder = Math.max(0, qty - l.stock)
                            const willFill = Math.min(qty, l.stock)
                            const variantParts = [l.year, l.medallionType, l.color].filter(Boolean)
                            return (
                                <tr key={l.inventoryId} className={qty === 0 ? 'opacity-50' : ''}>
                                    <td className="px-3 py-2">
                                        <div className="text-sm font-medium text-gray-900">{l.description}</div>
                                        <div className="text-xs text-gray-500 font-mono">
                                            {l.itemNumber}
                                            {variantParts.length > 0 && (
                                                <span className="ml-2 font-sans">— {variantParts.join(' / ')}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right text-sm">{l.stock}</td>
                                    <td className="px-3 py-2 text-right">
                                        <input
                                            type="number"
                                            min="0"
                                            value={l.quantity}
                                            onChange={(e) => {
                                                const n = parseInt(e.target.value, 10)
                                                setQty(l.inventoryId, isNaN(n) ? 0 : n)
                                            }}
                                            className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-sm">
                                        {qty === 0 ? (
                                            <span className="text-gray-400">Skipped</span>
                                        ) : willBackorder > 0 ? (
                                            <span className="text-amber-700">
                                                Fills {willFill}, backorders {willBackorder}
                                            </span>
                                        ) : (
                                            <span className="text-ghasc-success">In stock</span>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            {/* Notes */}
            <div>
                <label htmlFor="issue-notes" className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional, included on the receipt)
                </label>
                <textarea
                    id="issue-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="e.g. New group starting downtown"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy focus:border-transparent"
                />
            </div>

            {/* Totals + confirm */}
            <div className="border-t border-gray-100 pt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-gray-600">
                    GHASC covers{' '}
                    <span className="font-semibold text-gray-900">${totals.subtotal.toFixed(2)}</span>
                    {totals.backorderLines > 0 && (
                        <span className="text-amber-700">
                            {' '}· {totals.backorderLines} line{totals.backorderLines === 1 ? '' : 's'} will backorder
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="secondary" onClick={onDone} disabled={submitting}>Cancel</Button>
                    <Button type="button" onClick={handleConfirm} loading={submitting}>
                        Issue Package
                    </Button>
                </div>
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Line adapters — normalize the two sources into one editor/issue line shape
// ---------------------------------------------------------------------------
function lineFromPackageItem(it) {
    return {
        inventoryId: it.inventoryId,
        quantity: it.quantity,
        description: it.description,
        itemNumber: it.itemNumber,
        year: it.year,
        color: it.color,
        medallionType: it.medallionType,
        cost: it.cost,
        stock: it.stockQuantity,
    }
}

function lineFromInventory(item) {
    return {
        inventoryId: item.id,
        quantity: 1,
        description: item.description,
        itemNumber: item.itemNumber,
        year: item.year,
        color: item.color,
        medallionType: item.medallionType,
        cost: item.cost,
        stock: item.quantity,
    }
}

// ===========================================================================
//  Item picker — variant-aware (mirrors the Reorder page's picker)
// ===========================================================================
function ItemPicker({ allItems, loaded, existingIds, search, onSearchChange, onPick, onCancel }) {
    const [expandedKey, setExpandedKey] = useState(null)
    const [selectedVariant, setSelectedVariant] = useState({})

    const groups = useMemo(() => {
        const map = new Map()
        for (const item of allItems) {
            const key = `${item.category}|${item.description}|${item.medallionType || ''}`
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    category: item.category,
                    description: item.description,
                    medallionType: item.medallionType || null,
                    variants: [],
                })
            }
            map.get(key).variants.push(item)
        }
        const result = []
        for (const g of map.values()) {
            const remaining = g.variants.filter(v => !existingIds.has(v.id))
            if (remaining.length > 0) result.push({ ...g, variants: remaining })
        }
        return result
    }, [allItems, existingIds])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return groups.slice(0, 20)
        return groups
            .filter(g => {
                if (g.description.toLowerCase().includes(q)) return true
                if ((g.medallionType || '').toLowerCase().includes(q)) return true
                if ((g.category || '').toLowerCase().includes(q)) return true
                return g.variants.some(v =>
                    v.itemNumber.toLowerCase().includes(q) ||
                    (v.year || '').toLowerCase().includes(q) ||
                    (v.color || '').toLowerCase().includes(q)
                )
            })
            .slice(0, 50)
    }, [groups, search])

    function handleGroupClick(group) {
        if (group.variants.length === 1) {
            onPick(group.variants[0])
            return
        }
        if (expandedKey === group.key) {
            setExpandedKey(null)
        } else {
            setExpandedKey(group.key)
            setSelectedVariant(s => ({ ...s, [group.key]: group.variants[0].id }))
        }
    }

    function handleVariantSelect(groupKey, inventoryId) {
        setSelectedVariant(s => ({ ...s, [groupKey]: inventoryId }))
    }

    function handleAddVariant(group) {
        const id = selectedVariant[group.key]
        const variant = group.variants.find(v => v.id === id)
        if (!variant) return
        onPick(variant)
        setExpandedKey(null)
    }

    return (
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <Input
                        id="pkg-picker-search"
                        label="Find an item to add"
                        placeholder="Item number, description, year, color..."
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        autoFocus
                    />
                </div>
                <button onClick={onCancel} className="text-sm text-gray-500 hover:underline pb-2">
                    Cancel
                </button>
            </div>

            {!loaded ? (
                <div className="text-sm text-gray-500 py-2">Loading catalog...</div>
            ) : filtered.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">No matching items.</div>
            ) : (
                <ul className="divide-y divide-gray-200 max-h-96 overflow-y-auto bg-white rounded border border-gray-200">
                    {filtered.map(group => (
                        <li key={group.key}>
                            <PickerRow
                                group={group}
                                expanded={expandedKey === group.key}
                                selectedVariantId={selectedVariant[group.key]}
                                onClick={() => handleGroupClick(group)}
                                onVariantSelect={(id) => handleVariantSelect(group.key, id)}
                                onAdd={() => handleAddVariant(group)}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

function PickerRow({ group, expanded, selectedVariantId, onClick, onVariantSelect, onAdd }) {
    const isMulti = group.variants.length > 1
    const isTriplate = group.medallionType === 'Triplate'

    if (!isMulti) {
        const v = group.variants[0]
        const parts = [v.year, v.color].filter(Boolean)
        return (
            <button type="button" onClick={onClick} className="w-full px-3 py-2 hover:bg-gray-50 text-left">
                <div className="text-sm text-gray-900">{v.description}</div>
                <div className="text-xs text-gray-500 font-mono">
                    {v.itemNumber}
                    {parts.length > 0 && <span className="ml-2 font-sans">— {parts.join(' / ')}</span>}
                    <span className="ml-2 font-sans">· {v.quantity} in stock</span>
                </div>
            </button>
        )
    }

    const years = [...new Set(group.variants.map(v => v.year).filter(Boolean))].sort(naturalYearSort)
    const colors = isTriplate
        ? [...new Set(group.variants.map(v => v.color).filter(Boolean))].sort()
        : []

    const selected = group.variants.find(v => v.id === selectedVariantId) || group.variants[0]
    const selectedYear = selected?.year || ''
    const selectedColor = selected?.color || ''

    function pickYear(year) {
        const match = group.variants.find(v =>
            v.year === year && (isTriplate ? v.color === selectedColor : true)
        ) || group.variants.find(v => v.year === year)
        if (match) onVariantSelect(match.id)
    }

    function pickColor(color) {
        const match = group.variants.find(v => v.color === color && v.year === selectedYear)
            || group.variants.find(v => v.color === color)
        if (match) onVariantSelect(match.id)
    }

    return (
        <div>
            <button type="button" onClick={onClick} className="w-full px-3 py-2 hover:bg-gray-50 text-left">
                <div className="text-sm text-gray-900 flex items-center justify-between">
                    <span>
                        {group.medallionType
                            ? <>{group.medallionType} {group.description.includes(group.medallionType) ? '' : group.description}</>
                            : group.description}
                    </span>
                    <span className="text-xs text-gray-400">{expanded ? '▾' : '▸'}</span>
                </div>
                <div className="text-xs text-gray-500">
                    {group.variants.length} variant{group.variants.length === 1 ? '' : 's'}
                    <span className="ml-1">— pick a year{isTriplate ? ' and color' : ''}</span>
                </div>
            </button>

            {expanded && (
                <div className="px-3 pb-3 pt-1 bg-gray-50 border-t border-gray-200">
                    <div className="grid sm:grid-cols-2 gap-2 mb-2">
                        <div>
                            <label className="block text-xs text-gray-600 mb-0.5">Year</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => pickYear(e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                            >
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        {isTriplate && colors.length > 0 && (
                            <div>
                                <label className="block text-xs text-gray-600 mb-0.5">Color</label>
                                <select
                                    value={selectedColor}
                                    onChange={(e) => pickColor(e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                >
                                    {colors.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                        <span className="font-mono">
                            {selected?.itemNumber}
                            {' · '}{selected?.quantity || 0} in stock
                            {' · '}${Number(selected?.cost || 0).toFixed(2)}
                        </span>
                        <button
                            type="button"
                            onClick={onAdd}
                            className="px-3 py-1 bg-ghasc-navy text-white rounded text-sm font-medium hover:opacity-90"
                        >
                            Add
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

function naturalYearSort(a, b) {
    const parseLabel = (s) => {
        const m = /^(\d+)\s*(month|year)/i.exec(s)
        if (!m) return [99999, s]
        const num = parseInt(m[1], 10)
        const unit = m[2].toLowerCase()
        return [unit === 'month' ? num : 1000 + num, s]
    }
    const [an, as] = parseLabel(a)
    const [bn, bs] = parseLabel(b)
    if (an !== bn) return an - bn
    return as.localeCompare(bs)
}